class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } }).then(() =>
              CartPerformance.measureFromEvent('note-update:user-action', event)
            );
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      constructor() {
        super();
        this.boundHandleApply = this.handleApply.bind(this);
        this.boundHandleRemove = this.handleRemove.bind(this);
      }

      connectedCallback() {
        this.attachEventListeners();
      }

      attachEventListeners() {
        // Use event delegation to handle dynamically added elements
        this.removeEventListener('submit', this.boundHandleApply);
        this.removeEventListener('click', this.boundHandleRemove);

        this.addEventListener('submit', (event) => {
          if (event.target.classList.contains('discount-form')) {
            this.boundHandleApply(event);
          }
        });

        this.addEventListener('click', (event) => {
          const removeButton = event.target.closest('.discount-applied__remove');
          if (removeButton) {
            this.boundHandleRemove(event);
          }
        });
      }

      get form() {
        return this.querySelector('.discount-form');
      }

      get input() {
        return this.querySelector('.discount-form__input');
      }

      get button() {
        return this.querySelector('.discount-form__button');
      }

      get errorElement() {
        return this.querySelector('.discount-error');
      }

      get removeButton() {
        return this.querySelector('.discount-applied__remove');
      }

      async handleApply(event) {
        event.preventDefault();

        const discountCode = this.input.value.trim();
        if (!discountCode) {
          this.showError('Please enter a discount code');
          return;
        }

        this.setLoading(true);
        this.hideError();

        try {
          // Apply discount code to cart attributes
          const response = await fetch(`${routes.cart_url}/update.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              attributes: {
                discount_code: discountCode
              }
            })
          });

          if (!response.ok) {
            throw new Error('Failed to apply discount');
          }

          const cartData = await response.json();

          // Store discount code in session storage
          sessionStorage.setItem('shopify_discount_code', discountCode);

          // Update the checkout button to include the discount code
          this.updateCheckoutButton(discountCode);

          // Publish cart update event to trigger refresh
          if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-discount',
              cartData: cartData
            });
          }

          // Reload the cart drawer to show the discount
          await this.reloadCart();

        } catch (error) {
          console.error('Error applying discount:', error);
          this.showError('Unable to apply discount code');
          this.setLoading(false);
        }
      }

      async reloadCart() {
        // Reload the cart drawer to show updated discount
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
          await cartDrawerItems.onCartUpdate();
        } else {
          // Fallback: fetch and update cart drawer content
          try {
            const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
            const text = await response.text();
            const html = new DOMParser().parseFromString(text, 'text/html');

            const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
            for (const selector of selectors) {
              const targetElement = document.querySelector(selector);
              const sourceElement = html.querySelector(selector);
              if (targetElement && sourceElement) {
                targetElement.replaceWith(sourceElement);
              }
            }
          } catch (error) {
            console.error('Error reloading cart:', error);
            // If reload fails, just show the applied discount UI
            this.showAppliedDiscount(sessionStorage.getItem('shopify_discount_code'));
          }
        }
      }

      updateCheckoutButton(discountCode) {
        const checkoutButton = document.getElementById('CartDrawer-Checkout');
        if (checkoutButton) {
          const currentForm = checkoutButton.getAttribute('form');
          if (currentForm) {
            const form = document.getElementById(currentForm);
            if (form) {
              // Update form action to include discount
              form.setAttribute('action', `/checkout?discount=${encodeURIComponent(discountCode)}`);
            }
          } else {
            // If no form, update the button's onclick
            checkoutButton.setAttribute('onclick', `window.location.href='/checkout?discount=${encodeURIComponent(discountCode)}'`);
          }
        }
      }

      showAppliedDiscount(discountCode) {
        // Create applied discount UI
        const appliedHTML = `
          <div class="discount-applied">
            <div class="discount-applied__info">
              <span class="discount-applied__icon">
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-discount" fill="none" viewBox="0 0 12 12">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 102 0 1 1 0 00-2 0z" fill="currentColor"/>
                </svg>
              </span>
              <span class="discount-applied__text">
                ${this.escapeHtml(discountCode)}
                (Applied at checkout)
              </span>
            </div>
            <button
              type="button"
              class="discount-applied__remove"
              aria-label="Remove discount code"
              data-discount-code="${this.escapeHtml(discountCode)}"
            >
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                  <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
                </svg>
              </span>
            </button>
          </div>
        `;

        // Replace the form with applied discount
        this.innerHTML = appliedHTML;
        // Event listeners will be attached via event delegation in attachEventListeners
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }

      async handleRemove(event) {
        event.preventDefault();

        try {
          // Remove discount code from cart attributes
          const response = await fetch(`${routes.cart_url}/update.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              attributes: {
                discount_code: ''
              }
            })
          });

          if (!response.ok) {
            throw new Error('Failed to remove discount');
          }

          // Remove discount code from session storage
          sessionStorage.removeItem('shopify_discount_code');

          // Reset the checkout button
          const checkoutButton = document.getElementById('CartDrawer-Checkout');
          if (checkoutButton) {
            const currentForm = checkoutButton.getAttribute('form');
            if (currentForm) {
              const form = document.getElementById(currentForm);
              if (form) {
                form.setAttribute('action', '/checkout');
              }
            }
            checkoutButton.removeAttribute('onclick');
          }

          // Reload the cart to remove the discount display
          await this.reloadCart();

        } catch (error) {
          console.error('Error removing discount:', error);
          // Fallback to just showing the form if API call fails
          this.showDiscountForm();
        }
      }

      showDiscountForm() {
        const formHTML = `
          <form class="discount-form" onsubmit="return false;">
            <input
              type="text"
              class="discount-form__input"
              placeholder="Discount code"
              aria-label="Discount code"
              name="discount"
            >
            <button type="submit" class="discount-form__button">
              Apply
            </button>
          </form>
          <div class="discount-error" style="display: none;"></div>
        `;

        this.innerHTML = formHTML;
        // Event listeners will be attached via event delegation in attachEventListeners
      }

      setLoading(loading) {
        if (this.button) {
          this.button.disabled = loading;
          this.button.textContent = loading ? 'Applying...' : 'Apply';
        }
        if (this.input) {
          this.input.disabled = loading;
        }
      }

      showError(message) {
        if (this.errorElement) {
          this.errorElement.textContent = message;
          this.errorElement.style.display = 'block';
        }
      }

      hideError() {
        if (this.errorElement) {
          this.errorElement.style.display = 'none';
          this.errorElement.textContent = '';
        }
      }
    }
  );
}
